import { syncAchievements } from '../services/achievementService';
import { NotificationSoundSettings } from '../components/NotificationSoundSettings';
import { BRAND_ACCENTS } from '../constants';

const ALL_LANG_MAP: Record<string, { name: string; flag: string }> = {
  en: { name: 'Inglês', flag: '🇺🇸' },
  es: { name: 'Espanhol', flag: '🇪🇸' },
  fr: { name: 'Francês', flag: '🇫🇷' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  de: { name: 'Alemão', flag: '🇩🇪' },
  pt: { name: 'Português', flag: '🇧🇷' },
  jp: { name: 'Japonês', flag: '🇯🇵' },
  cn: { name: 'Mandarim', flag: '🇨🇳' },
  ru: { name: 'Russo', flag: '🇷🇺' },
  hu: { name: 'Húngaro', flag: '🇭🇺' }
};


import React, { useState, useEffect } from 'react';
import { FloatingTourButton } from '../components/FloatingTourButton';
import { useSimpleTour } from '../hooks/useSimpleTour';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, HelpCircle, Languages, Globe, ChevronRight, Info, RotateCw, Sparkles, Check, ChevronLeft,
  Palette, Fingerprint, User, MessageCircle, MoreVertical, X, XCircle, Volume2, Download, Trash2, Loader2, LogOut, Cloud, AlertCircle, Trophy, BrainCircuit, Mic, ChevronDown, FlaskConical, Bell, Lock, BookOpen
} from 'lucide-react';
import { loadAchievements, UserAchievement } from '../services/achievementService';
import { googleDriveService } from '../services/googleDriveService';
import { useOfflineVoices } from '../hooks/useOfflineVoices';
import { PageTourButton } from '../components/PageTour';
import type { TourKey } from '../tours/allTours';
import { isDictOfflineEnabled, estimateDictOfflineSizeMB, downloadDictOffline, deleteDictOffline } from '../services/dictionaryOfflineService';
import { offlineTtsEngine } from '../services/offlineTtsEngine';
import { bergamotModelService } from '../services/bergamotModelService';
import { nllbModelService, checkNllbModelDownloaded, downloadNllbModel, deleteNllbModel } from '../services/nllbModelService';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { dbManager } from '../services/db_manager';
import { supabase } from '../services/supabaseClient';
import { profileService } from '../services/profileService';
import { APP_VERSION } from '../constants';
import { performHardReset } from '../utils/updateUtils';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import { AppBackButton } from '../components/AppBackButton';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { motion, AnimatePresence } from 'framer-motion';
import T from '../components/T';

const updateModalTranslations: Record<string, { title: string; confirm: string; close: string }> = {
  pt: { title: "Deseja atualizar o aplicativo agora?", confirm: "Confirmar", close: "Fechar" },
  en: { title: "Do you want to update the application now?", confirm: "Confirm", close: "Close" },
  es: { title: "¿Deseas actualizar la aplicación ahora?", confirm: "Confirmar", close: "Cerrar" },
  fr: { title: "Voulez-vous mettre à jour l'application maintenant ?", confirm: "Confirmer", close: "Fermer" },
  de: { title: "Möchten Sie die Anwendung jetzt aktualisieren?", confirm: "Bestätigen", close: "Schließen" },
  it: { title: "Vuoi aggiornare l'applicazione adesso?", confirm: "Conferma", close: "Chiudi" },
  hu: { title: "Frissíteni szeretné az alkalmazást most?", confirm: "Megerősítés", close: "Bezárás" },
  ja: { title: "今すぐアプリを更新しますか？", confirm: "確認", close: "閉じる" },
  zh: { title: "您想现在更新应用程序吗？", confirm: "确认", close: "关闭" },
  ru: { title: "Вы хотите обновить приложение сейчас?", confirm: "Подтвердить", close: "Закрыть" }
};

const ConfirmationModal: React.FC<{ isOpen: boolean; title: string; message: string; onConfirm: () => void; onCancel: () => void; confirmLabel?: string; cancelLabel?: string; isDanger?: boolean; hideCancel?: boolean; }> = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', isDanger = false, hideCancel = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 bg-slate-950/50 backdrop-blur-md animate-in fade-in duration-300" onClick={onCancel}>
      <div className="bg-surface p-8 rounded-[28px] w-full max-w-sm text-center border border-black/[0.06] dark:border-white/[0.06] animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500" onClick={e => e.stopPropagation()}>
        <div className={`w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center text-ink ${isDanger ? 'bg-tint-rose' : 'bg-tint-lavender'}`}><AlertCircle size={22} strokeWidth={1.75} /></div>
        <h3 className="text-xl font-medium tracking-[-0.02em] text-ink mb-2">{title}</h3>
        {message ? <p className="text-muted text-[13px] leading-relaxed mb-8">{message}</p> : <div className="mb-6" />}
        <div className="flex flex-col gap-2">
          <button onClick={onConfirm} className={`h-12 rounded-full text-[13px] font-medium transition-all duration-300 hover:opacity-90 active:scale-[0.98] ${isDanger ? 'bg-bad text-white' : 'bg-ink text-surface'}`}>{confirmLabel}</button>
          {!hideCancel && <button onClick={onCancel} className="h-11 rounded-full text-muted hover:text-ink text-[13px] font-medium transition-colors duration-300">{cancelLabel}</button>}
        </div>
      </div>
    </div>
  );
};

type SettingsTab = 'preferencias' | 'translation' | 'offline' | 'update' | 'upgrade' | 'storage' | 'sounds';

export default function SettingsPage() {


  useSimpleTour('SETTINGS', [{ element: '.min-h-screen > div:first-child', title: 'Configurações', description: 'Personalize o app, vincule sua conta do Google Drive e ajuste preferências.' }]);

  const navigate = useNavigate();
  const { user, login, logout } = useAuth();

  // Google Drive state
  const [driveToken, setDriveToken] = useState<string | null>(googleDriveService.getToken());
  const [driveUser, setDriveUser] = useState<any | null>(googleDriveService.getUserInfo());
  const [isDriveConnecting, setIsDriveConnecting] = useState(false);
  const [driveError, setDriveError] = useState<string | null>(null);
  const [sdkLoading, setSdkLoading] = useState(false);
  const {
    targetLanguage, setTargetLanguage,
    studyLanguage, setStudyLanguage,
    translationMode, setTranslationMode,
    preferredTranslator, setPreferredTranslator,
    isSmartTranslationEnabled, setSmartTranslationEnabled,
    accentColor, setAccentColor
  } = useTheme();

  const { profile, refreshProfile, refreshBooks } = useData();
  const { t, i18n } = useTranslation();

  const [isOfflineTranslatorEnabled, setIsOfflineTranslatorEnabled] = useState<boolean>(() => {
    return localStorage.getItem('libritui_offline_translator_enabled') === 'true';
  });

  const [isPrerecordedAudioEnabledState, setIsPrerecordedAudioEnabledState] = useState<boolean>(() => {
    return localStorage.getItem('libritui_use_prerecorded_audio') !== 'false';
  });

  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isDownloaded, setIsDownloaded] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const checkDownloaded = async () => {
      try {
        const downloaded = await checkNllbModelDownloaded();
        if (active) {
          setIsDownloaded(downloaded);
        }
      } catch (err) {
        console.error("Error checking NLLB ONNX cache:", err);
      }
    };
    checkDownloaded();
    return () => {
      active = false;
    };
  }, []);

  const handleDownload = async () => {
    setDownloadProgress(0);
    setDownloadError(null);
    try {
      await downloadNllbModel((percent) => {
        setDownloadProgress(percent);
      });
      setIsDownloaded(true);
      setDownloadProgress(null);
    } catch (err: any) {
      console.error("Download failed:", err);
      setDownloadError(err.message || "Erro no download");
      setDownloadProgress(null);
    }
  };

  // Dicionário offline (palavras) — PRO
  const [dictDownloaded, setDictDownloaded] = useState<boolean>(() => isDictOfflineEnabled());
  const [dictProgress, setDictProgress] = useState<number | null>(null);
  const [dictSizeMB, setDictSizeMB] = useState<number | null>(null);
  const [dictError, setDictError] = useState<string | null>(null);
  const nativeLangCode = (localStorage.getItem('i18nextLng') || 'pt').split('-')[0];

  // Mede o tamanho do dicionário só com a aba Tradução aberta (o resultado fica em cache local)
  const { tab: routeTab } = useParams();
  useEffect(() => {
    if (routeTab !== 'translation') return;
    let active = true;
    estimateDictOfflineSizeMB(nativeLangCode).then(mb => { if (active) setDictSizeMB(mb); });
    return () => { active = false; };
  }, [nativeLangCode, routeTab]);

  const handleDictDownload = async () => {
    setDictError(null);
    setDictProgress(0);
    try {
      await downloadDictOffline(nativeLangCode, setDictProgress);
      setDictDownloaded(true);
    } catch (err: any) {
      setDictError(err?.message || 'Erro no download');
    } finally {
      setDictProgress(null);
    }
  };

  const handleDictDelete = async () => {
    await deleteDictOffline();
    setDictDownloaded(false);
  };

  const handleDelete = async () => {
    try {
      await deleteNllbModel();
      setIsDownloaded(false);
    } catch (err: any) {
      console.error("Delete failed:", err);
    }
  };

  const languages = [
    { name: t('languages.en'), flag: '🇺🇸', code: 'en' },
    { name: t('languages.fr'), flag: '🇫🇷', code: 'fr' },
    { name: t('languages.it'), flag: '🇮🇹', code: 'it' },
    { name: t('languages.es'), flag: '🇪🇸', code: 'es' },
    { name: t('languages.de'), flag: '🇩🇪', code: 'de' },
    { name: t('languages.ja'), flag: '🇯🇵', code: 'ja' },
    { name: t('languages.zh'), flag: '🇨🇳', code: 'zh' },
    { name: t('languages.pt'), flag: '🇧🇷', code: 'pt' },
    { name: t('languages.ru'), flag: '🇷🇺', code: 'ru' },
    { name: t('languages.hu'), flag: '🇭🇺', code: 'hu' }
  ];
  const { tab } = useParams();
  
  const urlToTabMap: Record<string, SettingsTab> = {
    'preferences': 'preferencias',
    'translation': 'translation',
    'offline': 'offline',
    'updates': 'update',
    'upgrade': 'upgrade',
    'sync': 'storage',
    'notifications-and-sounds': 'sounds'
  };

  const tabToUrlMap: Record<SettingsTab, string> = {
    'preferencias': 'preferences',
    'translation': 'translation',
    'offline': 'offline',
    'update': 'updates',
    'upgrade': 'upgrade',
    'storage': 'sync',
    'sounds': 'notifications-and-sounds'
  };

  const activeTab: SettingsTab = (tab && urlToTabMap[tab]) ? urlToTabMap[tab] : 'preferencias';

  const setActiveTab = (newTab: SettingsTab) => {
    navigate(`/settings/${tabToUrlMap[newTab]}`, { replace: true });
  };

  // Acordeão (Design 2.0): só a seção da URL fica aberta; clicar de novo fecha.
  const openSection: SettingsTab | null = (tab && urlToTabMap[tab]) ? urlToTabMap[tab] : null;
  const toggleSection = (section: SettingsTab) => {
    if (openSection === section) navigate('/settings', { replace: true });
    else setActiveTab(section);
  };

  const [isUpgrading, setIsUpgrading] = useState(false);

  useEffect(() => {
    if (activeTab === 'storage') {
      setSdkLoading(true);
      googleDriveService.loadGapiAndGis()
        .then(() => setSdkLoading(false))
        .catch(err => {
          console.error("Erro ao carregar SDK do Google:", err);
          setDriveError("Falha ao inicializar o SDK do Google. Por favor, recarregue a página.");
          setSdkLoading(false);
        });
    }
  }, [activeTab]);

  const handleConnectDrive = () => {
    setIsDriveConnecting(true);
    setDriveError(null);
    googleDriveService.connect(
      (token, userInfo) => {
        setDriveToken(token);
        setDriveUser(userInfo);
        setIsDriveConnecting(false);
        
        // Sincroniza imediatamente em segundo plano para reidratar os livros
        sessionStorage.setItem('libritui_drive_synced', 'true');
        googleDriveService.syncFromDrive()
          .then(async (res) => {
            console.log(`[SettingsPage] Sincronização inicial finalizada em background. Livros importados: ${res.importedCount}`);
            if (res.importedCount > 0) {
              await refreshBooks(true);
            }
          })
          .catch((err) => {
            console.error('[SettingsPage] Falha na sincronização de background inicial:', err);
            sessionStorage.removeItem('libritui_drive_synced');
          });
      },
      (err) => {
        console.error("Google Drive connection error:", err);
        setDriveError("Não foi possível conectar ao Google Drive. Verifique suas credenciais e tente novamente.");
        setIsDriveConnecting(false);
      }
    );
  };

  const handleDisconnectDrive = async () => {
    try {
      const { storageService } = await import('../services/storageService');
      const { fileStorageService } = await import('../services/fileStorageService');
      const books = storageService.getAllBooks();
      const driveBooks = books.filter(b => b.fileUrl && b.fileUrl.startsWith('indexeddb://'));
      for (const b of driveBooks) {
        if (b.id) {
          storageService.deleteBook(b.id);
          await fileStorageService.deleteFile(b.id);
        } else if (b.custom_id) {
          storageService.deleteBook(b.custom_id);
          await fileStorageService.deleteFile(b.custom_id);
        }
      }
    } catch(err) {
      console.error('Error deleting local drive books:', err);
    }

    googleDriveService.disconnect();
    setDriveToken(null);
    setDriveUser(null);
    sessionStorage.removeItem('libritui_drive_synced');
    setShowDisconnectModal(false);
  };
  const [subscriptionTier, setSubscriptionTier] = useState<string>('free');
  // Downloads para uso offline (tradutor, dicionário e vozes) são exclusivos do PRO
  const isProUser = subscriptionTier === 'pro' || (profile as any)?.plan_type === 'pro' || (profile as any)?.subscription_tier === 'pro';

  /** Linha "bloqueado" mostrada a usuários FREE no lugar de um botão de download. */
  const ProLockedRow: React.FC<{ label: string; size?: string }> = ({ label, size }) => (
    <button
      type="button"
      onClick={() => setActiveTab('upgrade')}
      className="w-full flex items-center justify-between gap-3 bg-surface/60 p-4 rounded-2xl border border-dashed border-black/[0.1] dark:border-white/[0.1] text-left cursor-pointer hover:bg-surface transition-colors"
    >
      <span className="min-w-0">
        <span className="block font-medium text-sm text-ink/60">{label}</span>
        <span className="block text-[11px] text-muted mt-0.5">{t('offlineTr.proOnlyDesc')}</span>
      </span>
      <span className="flex items-center gap-1.5 shrink-0 font-mono text-[11px] text-warn bg-tint-butter px-3 h-8 rounded-full">
        <Lock size={12} />
        <span>{t('offlineTr.proOnly', 'Recurso PRO')}{size ? ` · ${size}` : ''}</span>
      </span>
    </button>
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [showDeleteVoiceModal, setShowDeleteVoiceModal] = useState<string | null>(null);
  const [isCancelCooldown, setIsCancelCooldown] = useState(false);
  const { voices, isInstalled, downloadVoice, deleteVoice } = useOfflineVoices();

  const handleCancelDownload = () => {
    setDownloadProgress(null);
    setIsCancelCooldown(true);
    setTimeout(() => {
      setIsCancelCooldown(false);
    }, 3000);
  };

  // Profile Edit State
  const [tempDisplayName, setTempDisplayName] = useState('');
  const [tempUsername, setTempUsername] = useState('');
  const [tempBio, setTempBio] = useState('');
  const [tempColor, setTempColor] = useState(accentColor);
  const [isSavingPref, setIsSavingPref] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<{code: string; name: string} | null>(null);
  // Display settings state
  const [showLevel, setShowLevel] = useState<boolean>(false);
  const [selectedLevelLang, setSelectedLevelLang] = useState<string>('fr');
  const [selectedLevelCefr, setSelectedLevelCefr] = useState<string>('B1');
  const [showAchievements, setShowAchievements] = useState<boolean>(false);
  // Só salva as preferências de exibição depois de carregá-las (senão os padrões desligados sobrescrevem o que estava salvo)
  const displayLoadedRef = React.useRef(false);

  // Multi-select para idiomas testados e seleção única para conquistas
  const [testedLanguages, setTestedLanguages] = useState<{ code: string; name: string; flag: string; level: string }[]>([]);
  const [selectedLevelLangs, setSelectedLevelLangs] = useState<string[]>([]);

  const [unlockedAchievements, setUnlockedAchievements] = useState<UserAchievement[]>([]);
  const [selectedAchievementKey, setSelectedAchievementKey] = useState<string | null>(null);

  // Auto-save display settings to localStorage & background upsert to D1 whenever changed
  useEffect(() => {
    if (!user?.id || !displayLoadedRef.current) return;
    const testedLevelsMap: Record<string, string> = {};
    testedLanguages.forEach(t => { testedLevelsMap[t.code] = t.level; });
    const selectedAch = unlockedAchievements.find(a => a.def.key === selectedAchievementKey);

    const displaySettings = {
      showLevel,
      selectedLevelLangs,
      testedLevels: testedLevelsMap,
      selectedLevelLang: selectedLevelLangs[0] || selectedLevelLang || 'fr',
      selectedLevelCefr: testedLevelsMap[selectedLevelLangs[0]] || selectedLevelCefr || 'B1',
      showAchievements,
      selectedAchievementKey,
      selectedAchievementName: selectedAch?.def.name || null,
      selectedAchievementIcon: selectedAch?.def.lucideIcon || null,
    };

    localStorage.setItem(`libritui_display_settings_${user.id}`, JSON.stringify(displaySettings));

    dbManager.from('profiles', user.id).upsert({
      id: user.id,
      display_settings: JSON.stringify(displaySettings),
      updated_at: new Date().toISOString()
    }).catch(err => console.error('[Settings] Auto-save display settings error:', err));
  }, [user?.id, showLevel, selectedLevelLangs, showAchievements, selectedAchievementKey, testedLanguages, unlockedAchievements]);

  // Busca histórico de testes e conquistas no D1
  useEffect(() => {
    if (!user?.id || user.isGuest) return;

    dbManager.from('user_language_tests', user.id).select({}, { orderBy: 'created_at DESC' })
      .then((res: any) => {
        const rows = Array.isArray(res) ? res : (res?.data || []);
        const langMap = new Map<string, string>();
        rows.forEach((r: any) => {
          const code = (r.lang_code || r.language || '').toLowerCase();
          const level = r.cefr_level || r.level || 'B1';
          if (code && !langMap.has(code)) {
            langMap.set(code, level);
          }
        });

        const list = Array.from(langMap.entries()).map(([code, level]) => {
          const info = ALL_LANG_MAP[code] || { name: code.toUpperCase(), flag: '🌐' };
          return { code, name: info.name, flag: info.flag, level };
        });

        setTestedLanguages(list);
      })
      .catch(err => console.error('[Settings] Erro ao carregar testes de nível:', err));

    syncAchievements(user.id).catch(() => []).then(() => loadAchievements(user.id)).then(achList => {
      const unlocked = achList.filter(a => a.level > 0);
      setUnlockedAchievements(unlocked);
    }).catch(err => console.error('[Settings] Erro ao carregar conquistas:', err));
  }, [user?.id]);

  useEffect(() => {
    if (profile) {
      let settings: any = null;
      if ((profile as any).display_settings) {
        try {
          settings = typeof (profile as any).display_settings === 'string'
            ? JSON.parse((profile as any).display_settings)
            : (profile as any).display_settings;
        } catch (e) {}
      } else if (user?.id) {
        const local = localStorage.getItem(`libritui_display_settings_${user.id}`);
        if (local) {
          try { settings = JSON.parse(local); } catch (e) {}
        }
      }

      if (settings) {
        if (typeof settings.showLevel === 'boolean') setShowLevel(settings.showLevel);
        if (Array.isArray(settings.selectedLevelLangs)) {
          setSelectedLevelLangs(settings.selectedLevelLangs);
        } else if (settings.selectedLevelLang) {
          setSelectedLevelLangs([settings.selectedLevelLang]);
        }
        if (settings.selectedLevelLang) setSelectedLevelLang(settings.selectedLevelLang);
        if (settings.selectedLevelCefr) setSelectedLevelCefr(settings.selectedLevelCefr);
        if (typeof settings.showAchievements === 'boolean') setShowAchievements(settings.showAchievements);
        if (settings.selectedAchievementKey) setSelectedAchievementKey(settings.selectedAchievementKey);
      }
      displayLoadedRef.current = true;
    }
  }, [profile, user?.id]);

  const [usernameStatus, setUsernameStatus] = useState<{ available: boolean; checking: boolean; message: string | null }>({ available: true, checking: false, message: null });

  useEffect(() => {
    if (profile) {
      setTempDisplayName(profile.display_name || '');
      setTempUsername(profile.username || '');
      setTempBio(profile.bio || '');
      setSubscriptionTier(profile.plan_type || profile.subscription_tier || 'free');
    }
  }, [profile]);

  // Fetch obrigatório no D1 toda vez que a página for montada:
  useEffect(() => {
    if (!user || user.isGuest) return;
    refreshProfile(true);
    profileService.getProfile(user.id).then((data) => {
      if (data) {
        setTempDisplayName(data.display_name || '');
        setTempUsername(data.username || '');
        setTempBio(data.bio || '');
        setSubscriptionTier(data.subscription_tier || data.plan_type || 'free');
      }
    }).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    setTempColor(accentColor);
  }, [accentColor]);

  useEffect(() => {
    if (user) {
      profileService.getProfile(user.id)
        .then((data) => {
          if (data) setSubscriptionTier(data.subscription_tier || 'free');
        });
    }
  }, [user]);

  useEffect(() => {
    // Check if we just returned from a manual update
    const isSuccessPending = localStorage.getItem('libritui_update_success_pending') === 'true';
    if (isSuccessPending) {
      setShowSuccess(true);
      localStorage.removeItem('libritui_update_success_pending');
    }
  }, []);

  useEffect(() => {
    if (!tempUsername || !user || tempUsername === profile?.username) {
      setUsernameStatus({ available: true, checking: false, message: null });
      return;
    }

    const timer = setTimeout(async () => {
      setUsernameStatus(prev => ({ ...prev, checking: true }));
      try {
        const currentProfile = await profileService.getProfile(user.id);
        const ownOldUsernames: string[] = (() => {
          try {
            const raw = (currentProfile as any)?.old_usernames;
            if (!raw) return [];
            return typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
          } catch { return []; }
        })();
        const isOwnOldUsername = ownOldUsernames.includes(tempUsername.trim().toLowerCase());

        if (isOwnOldUsername) {
          setUsernameStatus({ available: true, checking: false, message: null });
          return;
        }

        const isAvailable = await profileService.checkUsernameAvailability(tempUsername, user.id);
        setUsernameStatus({
          available: isAvailable,
          checking: false,
          message: isAvailable ? null : t('settings.profile.username_taken', 'Este nome de usuário já está sendo usado')
        });
      } catch (err) {
        setUsernameStatus({ available: true, checking: false, message: null });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [tempUsername, user, profile?.username]);


  const handleHardRefresh = async () => {
    // We call performHardReset directly with the overlay and manual flag and target '/'
    await performHardReset('/', { showOverlay: true, isManual: true });
  };

  const handleSavePreferences = async () => {
    if (!user || isSavingPref) return;
    setIsSavingPref(true);
    setSaveStatus('saving');
    
    try {
      const bioValue = (tempBio || '').trim();
      const newUsername = tempUsername.trim().toLowerCase();

      const currentD1Profile = await profileService.getProfile(user.id);
      const currentUsername = currentD1Profile?.username || '';

      let oldUsernames: string[] = (() => {
        try {
          const raw = (currentD1Profile as any)?.old_usernames;
          if (!raw) return [];
          return typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
        } catch { return []; }
      })();

      if (currentUsername && currentUsername !== newUsername && !oldUsernames.includes(currentUsername)) {
        oldUsernames = [...oldUsernames, currentUsername];
      }

      const testedLevelsMap: Record<string, string> = {};
      testedLanguages.forEach(t => { testedLevelsMap[t.code] = t.level; });

      const selectedAch = unlockedAchievements.find(a => a.def.key === selectedAchievementKey);

      const displaySettings = {
        showLevel,
        selectedLevelLangs,
        testedLevels: testedLevelsMap,
        selectedLevelLang: selectedLevelLangs[0] || selectedLevelLang || 'fr',
        selectedLevelCefr: testedLevelsMap[selectedLevelLangs[0]] || selectedLevelCefr || 'B1',
        showAchievements,
        selectedAchievementKey,
        selectedAchievementName: selectedAch?.def.name || null,
        selectedAchievementIcon: selectedAch?.def.lucideIcon || null,
      };

      const updateData: any = {
        id: user.id,
        username: newUsername,
        display_name: tempDisplayName.trim(),
        bio: bioValue,
        theme_color: tempColor,
        display_settings: JSON.stringify(displaySettings),
        old_usernames: JSON.stringify(oldUsernames),
        updated_at: new Date().toISOString()
      };

      localStorage.setItem(`libritui_display_settings_${user.id}`, JSON.stringify(displaySettings));

      await dbManager.from('profiles', user.id).upsert(updateData);

      if (tempColor !== accentColor) {
        await setAccentColor(tempColor);
      }

      const optimisticProfile = {
          ...profile,
          ...updateData,
          theme_color: tempColor,
          old_usernames: oldUsernames
      };
      
      refreshProfile(true, optimisticProfile);
      
      setTempDisplayName(updateData.display_name);
      setTempUsername(updateData.username);
      setTempBio(bioValue);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);

    } catch (err) {
      console.error('[Settings] Error saving preferences:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    } finally {
      setIsSavingPref(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout error:', err);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  };

  const handleConfirmUpdate = () => {
    setShowSuccess(false);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'preferencias':
        return (
          <div className="space-y-8">
            <div className="space-y-8">

               <div data-tour="sec-pref-id" className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {/* ID Section */}
                 <div className="space-y-4">
                   <div className="space-y-2">
                     <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 group flex items-center gap-2">
                        <User size={10} /> {t('profile.settings.nickname', 'NOME DE EXIBIÇÃO')}
                     </label>
                     <input
                       type="text"
                       value={tempDisplayName}
                       onChange={(e) => setTempDisplayName(e.target.value)}
                       className="w-full bg-tint-stone/50 border border-transparent rounded-2xl px-5 py-3.5 text-sm text-ink outline-none focus:bg-surface focus:border-ink/15 transition-all duration-300"
                       placeholder={t('settings.profile.display_name_placeholder', 'Seu nome de exibição')}
                     />
                   </div>

                   <div className="space-y-2">
                     <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 group flex items-center gap-2">
                        <Fingerprint size={10} /> {t('settings.profile.username', 'USERNAME (@)')}
                     </label>
                     <input
                       type="text"
                       value={tempUsername}
                       onChange={(e) => {
                          const sanitized = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9]/g, '')
                            .slice(0, 20);
                          setTempUsername(sanitized);
                        }}
                        maxLength={20}
                       className="w-full bg-tint-stone/50 border border-transparent rounded-2xl px-5 py-3.5 text-sm text-ink outline-none focus:bg-surface focus:border-ink/15 transition-all duration-300"
                       placeholder="username"
                     />
                   </div>
                 </div>

                 {/* Bio Section */}
                 <div className="space-y-2">
                   <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 group flex items-center gap-2">
                      <MessageCircle size={10} /> {t('settings.profile.bio', 'BIOGRAFIA')}
                   </label>
                   <textarea
                     value={tempBio}
                     onChange={(e) => setTempBio(e.target.value)}
                     className="w-full bg-tint-stone/50 border border-transparent rounded-2xl px-5 py-3.5 h-[124px] text-sm text-ink outline-none focus:bg-surface focus:border-ink/15 transition-all duration-300 resize-none"
                     placeholder={t('settings.profile.bio_placeholder', 'Conte um pouco sobre você...')}
                   />
                 </div>
               </div>

               {/* Theme Color Section */}
               <div data-tour="sec-pref-theme" className="space-y-3">
                 <div className="flex items-center gap-3">
                   <Palette size={14} strokeWidth={1.75} className="text-muted" />
                   <span className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">COR PERSONALIZADA DO TEMA</span>
                 </div>
                 
                 <div className="bg-tint-stone/40 p-5 rounded-[22px] flex flex-col sm:flex-row items-center gap-6">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center shrink-0 transition-colors duration-500" 
                      style={{ backgroundColor: tempColor }}
                    >
                      <Sparkles size={28} className="text-white/40" />
                    </div>
                    
                    <div className="flex-1 w-full space-y-4">
                      <div className="flex flex-wrap gap-3">
                        {BRAND_ACCENTS.map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setTempColor(color)}
                            className={`w-9 h-9 rounded-full transition-all duration-300 shrink-0 hover:scale-110 ${tempColor === color ? 'ring-2 ring-offset-2 ring-ink ring-offset-surface scale-110' : 'opacity-80'}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                        <input 
                          type="color" 
                          value={tempColor} 
                          onChange={(e) => setTempColor(e.target.value)} 
                          className="w-12 h-10 cursor-pointer bg-transparent border-none rounded-lg" 
                        />
                        <input 
                          type="text" 
                          value={tempColor.toUpperCase()} 
                          onChange={(e) => setTempColor(e.target.value)} 
                          className="flex-1 bg-surface px-4 h-10 rounded-full font-mono text-xs border border-black/[0.06] dark:border-white/[0.06] text-ink" 
                        />
                      </div>
                    </div>
                 </div>
               </div>

               {/* Exibição no Perfil Section (Novas Opções) */}
               <div data-tour="sec-pref-display" className="space-y-3">
                 <div className="flex items-center gap-3">
                   <Sparkles size={14} strokeWidth={1.75} className="text-muted" />
                   <span className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em]"><T k="ui.settings.profileDisplay" /></span>
                 </div>

                 <div className="space-y-4 bg-tint-stone/40 p-5 rounded-[22px]">
                   {/* Toggle: Exibir meu nível */}
                   <div className="flex items-center justify-between">
                     <div>
                       <h4 className="font-medium text-sm dark:text-ink"><T k="ui.settings.showLevel" /></h4>
                       <p className="text-[10px] font-medium text-slate-400"><T k="ui.settings.showLevelHint" /></p>
                     </div>
                     <button
                       type="button"
                       onClick={() => setShowLevel(!showLevel)}
                       className={`relative inline-flex items-center h-7 w-12 shrink-0 cursor-pointer rounded-full p-0.5 transition-colors duration-300 ease-out ${
                         showLevel ? 'bg-ink' : 'bg-ink/15'
                       }`}
                     >
                       <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                         showLevel ? 'translate-x-5' : 'translate-x-0'
                       }`} />
                     </button>
                   </div>

                    {showLevel && (
                      <div className="pt-3 border-t border-black/[0.06] dark:border-white/[0.06] space-y-3 animate-in fade-in duration-200">
                        <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block">
                          SELECIONE OS IDIOMAS TESTADOS PARA EXIBIR NO PERFIL
                        </label>

                        {testedLanguages.length === 0 ? (
                          <div className="p-4 bg-warn/10 border border-warn/20 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                            <div className="flex items-center gap-3">
                              <BrainCircuit className="text-warn shrink-0" size={24} />
                              <div>
                                <h5 className="font-medium text-xs text-warn dark:text-warn"><T k="ui.settings.noLevelTest" /></h5>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium"><T k="ui.settings.noLevelTestHint" /></p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate('/level-test')}
                              className="h-9 px-4 bg-primary text-slate-900 rounded-full font-medium text-[12px] shrink-0 hover:opacity-90 active:scale-95 transition-all duration-300"
                            >
                              Fazer Teste
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2.5">
                            {testedLanguages.map(tLang => {
                              const isSelected = selectedLevelLangs.includes(tLang.code);
                              return (
                                <button
                                  key={tLang.code}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedLevelLangs(prev => prev.filter(c => c !== tLang.code));
                                    } else {
                                      setSelectedLevelLangs(prev => [...prev, tLang.code]);
                                    }
                                  }}
                                  className={`h-10 px-4 rounded-full font-medium text-xs flex items-center gap-2 border transition-all duration-300 cursor-pointer active:scale-95 ${
                                    isSelected
                                      ? 'bg-tint-sage text-ink border-transparent'
                                      : 'bg-surface text-muted border-black/[0.06] dark:border-white/[0.06] hover:text-ink'
                                  }`}
                                >
                                  <span className="text-base">{tLang.flag}</span>
                                  <span>{tLang.name}</span>
                                  <span className="px-2 py-0.5 bg-ok/20 rounded-md text-[10px]">{tLang.level}</span>
                                  {isSelected && <Check size={14} className="ml-1 text-ok" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Toggle: Exibir minhas conquistas */}
                    <div className="flex items-center justify-between pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
                      <div>
                        <h4 className="font-medium text-sm dark:text-ink">Exibir minhas conquistas</h4>
                        <p className="text-[10px] font-medium text-slate-400"><T k="ui.settings.showAchievementHint" /></p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAchievements(!showAchievements)}
                        className={`relative inline-flex items-center h-7 w-12 shrink-0 cursor-pointer rounded-full p-0.5 transition-colors duration-300 ease-out ${
                          showAchievements ? 'bg-ink' : 'bg-ink/15'
                        }`}
                      >
                        <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          showAchievements ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>

                    {showAchievements && (
                      <div className="pt-3 border-t border-black/[0.06] dark:border-white/[0.06] space-y-3 animate-in fade-in duration-200">
                        <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block">
                          SELECIONE UMA CONQUISTA DESBLOQUEADA PARA EXIBIR
                        </label>

                        {unlockedAchievements.length === 0 ? (
                          <div className="p-4 bg-warn/10 border border-warn/20 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                            <div className="flex items-center gap-3">
                              <Trophy className="text-warn shrink-0" size={24} />
                              <div>
                                <h5 className="font-medium text-xs text-warn dark:text-warn">Nenhuma conquista desbloqueada</h5>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium"><T k="ui.settings.keepGoing" /></p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate('/achievements')}
                              className="h-9 px-4 bg-primary text-slate-900 rounded-full font-medium text-[12px] shrink-0 hover:opacity-90 active:scale-95 transition-all duration-300"
                            >
                              Ver Conquistas
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2.5">
                            {unlockedAchievements.map(ach => {
                              const isSelected = selectedAchievementKey === ach.def.key;
                              return (
                                <button
                                  key={ach.def.key}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedAchievementKey(null);
                                    } else {
                                      setSelectedAchievementKey(ach.def.key);
                                    }
                                  }}
                                  className={`h-10 px-4 rounded-full font-medium text-xs flex items-center gap-2 border transition-all duration-300 cursor-pointer active:scale-95 ${
                                    isSelected
                                      ? 'bg-tint-butter text-ink border-transparent'
                                      : 'bg-surface text-muted border-black/[0.06] dark:border-white/[0.06] hover:text-ink'
                                  }`}
                                >
                                  <Trophy size={16} className={isSelected ? 'text-warn' : 'text-slate-400'} />
                                  <span>{ach.def.name}</span>
                                  <span className="px-2 py-0.5 bg-warn/20 rounded-md text-[10px]">Nível {ach.level}</span>
                                  {isSelected && <Check size={14} className="ml-1 text-warn" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                 </div>
               </div>

               {/* Action Footer */}
              <div data-tour="sec-pref-save" className="pt-2 space-y-2">
                <button
                   onClick={handleSavePreferences}
                   disabled={isSavingPref || !usernameStatus.available || usernameStatus.checking}
                   className={`w-full h-12 rounded-full text-[13px] font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-40 ${
                     saveStatus === 'success' ? 'bg-ok text-white' :
                     saveStatus === 'error' ? 'bg-bad text-white' :
                     'bg-ink text-surface hover:opacity-90 active:scale-[0.98]'
                   }`}
                >
                  {isSavingPref ? <RotateCw className="animate-spin" size={16} /> : 
                   saveStatus === 'success' ? <Check size={16} /> :
                   saveStatus === 'error' ? <X size={16} /> :
                   <Sparkles size={16} />}
                  
                  {isSavingPref ? t('common.saving') : 
                   saveStatus === 'success' ? t('common.success') :
                   saveStatus === 'error' ? t('profile.settings.saveError', 'Erro ao Salvar') :
                   t('profile.settings.saveChanges')}
                </button>

                <button
                  onClick={() => setShowLogoutModal(true)}
                  className="group w-full h-12 rounded-full text-bad text-[13px] font-medium border border-bad/25 hover:bg-tint-rose transition-all duration-300 flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  <LogOut size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  {t('auth.logout', 'Sair da conta')}
                </button>
              </div>
            </div>
          </div>
        );
      case 'translation':
        return (
          <div className="space-y-3">
            {/* Card 1: Idioma Nativo */}
            <div data-tour="sec-tr-native" className="bg-tint-stone/40 p-5 md:p-6 rounded-[22px] space-y-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2.5 text-ink font-medium text-[15px] tracking-[-0.01em] [&_svg]:text-muted [&_svg]:w-4 [&_svg]:h-4">
                  <Globe size={18} />
                  <span>{t('settings.translation.nativeLanguage', 'Idioma Nativo')}</span>
                </div>
                <p className="text-[13px] text-muted leading-relaxed">
                  {t('settings.translation.description', 'Seu idioma de referência para traduções, definições de vocabulário e explicações.')}
                </p>
              </div>

              <div className="max-w-md pt-2">
                <div className="relative group">
                  <select
                    value={languages.find(l => l.code === (localStorage.getItem('i18nextLng') || 'pt'))?.name || targetLanguage}
                    onChange={(e) => {
                      const selected = languages.find(l => l.name === e.target.value);
                      if (selected) {
                        setPendingLanguage(selected);
                      }
                    }}
                    className="w-full appearance-none bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-full px-5 h-12 text-sm text-left outline-none focus:border-ink/20 transition-all text-ink cursor-pointer"
                  >
                    {languages.map(l => <option key={l.code} value={l.name}>{l.flag} {l.name}</option>)}
                  </select>
                  <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400"><ChevronRight className="rotate-90" size={16} /></div>
                </div>
              </div>
            </div>

            {/* Separação entre o idioma nativo e os recursos offline */}
            <div className="py-3" aria-hidden="true">
              <div className="h-px bg-gradient-to-r from-transparent via-black/[0.10] dark:via-white/[0.10] to-transparent" />
            </div>

            {/* Card 2: Tradutor Offline */}
            <div data-tour="sec-tr-offline" className="bg-tint-stone/40 p-5 md:p-6 rounded-[22px] space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5 text-ink font-medium text-[15px] tracking-[-0.01em] [&_svg]:text-muted [&_svg]:w-4 [&_svg]:h-4">
                    <Download size={18} />
                    <span>{t('settings.translation.offlineTranslator', 'Tradutor Offline')}</span>
                  </div>
                  <p className="text-[13px] text-muted leading-relaxed">
                    {t('settings.translation.offlineTranslatorDesc', 'Tradutor neural offline para palavras e frases (em desenvolvimento).')}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const newValue = !isOfflineTranslatorEnabled;
                    if (newValue && !isProUser) {
                      setDownloadError(t('offlineTr.proOnlyDesc'));
                      return;
                    }
                    if (newValue && !isDownloaded) {
                      setDownloadError(t('settings.translation.mustDownloadFirst', 'Baixe a tradução offline no botão abaixo para habilitar esta função.'));
                      return;
                    }
                    setIsOfflineTranslatorEnabled(newValue);
                    localStorage.setItem('libritui_offline_translator_enabled', newValue ? 'true' : 'false');
                  }}
                  className={`relative inline-flex items-center h-7 w-12 shrink-0 cursor-pointer rounded-full p-0.5 transition-colors duration-300 ease-out focus:outline-none ${
                    isOfflineTranslatorEnabled ? 'bg-ink' : 'bg-ink/15'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isOfflineTranslatorEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Status e Download do Modelo (frases completas) */}
              <div className="pt-2 space-y-3">
                {!isProUser && !isDownloaded && downloadProgress === null ? (
                  <ProLockedRow label={t('offlineTr.sentenceTranslator', 'Baixar tradutor (frases completas)')} size="900MB" />
                ) : downloadProgress !== null ? (
                  <div className="space-y-2 bg-surface p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
                    <div className="flex justify-between items-center text-xs font-medium text-slate-600 dark:text-slate-400">
                      <span>{t('settings.translation.downloadingOfflineModels', 'Baixando modelos...')}</span>
                      <div className="flex items-center gap-3">
                        <span>{downloadProgress}% de 900MB</span>
                        <button
                          type="button"
                          onClick={handleCancelDownload}
                          title="Cancelar download"
                          className="p-1 rounded-full text-bad hover:bg-tint-rose dark:hover:bg-tint-rose/40 transition-colors cursor-pointer"
                        >
                          <XCircle size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-ink transition-all duration-300"
                        style={{ width: `${downloadProgress}%` }}
                      />
                    </div>
                  </div>
                ) : isDownloaded ? (
                  <div className="flex items-center justify-between bg-tint-sage dark:bg-tint-sage/20 p-4 rounded-2xl border border-tint-sage dark:border-tint-sage/30 animate-in fade-in duration-300">
                    <div className="flex items-center gap-2 text-ok dark:text-ok font-medium text-sm">
                      <Check size={18} />
                      <span>{t('offlineTr.sentenceReady', 'Tradutor de frases pronto')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">900MB</span>
                      <button
                        type="button"
                        onClick={handleDelete}
                        title={t('settings.translation.deleteOfflineModel', 'Excluir modelos do dispositivo')}
                        className="p-2 text-slate-400 hover:text-bad transition-colors cursor-pointer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={isCancelCooldown}
                    onClick={handleDownload}
                    className="w-full flex items-center justify-between bg-surface hover:bg-tint-stone/60 p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] transition-all duration-300 cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="font-medium text-sm text-slate-700 dark:text-slate-300">
                      {isCancelCooldown 
                        ? 'Aguarde 3 segundos para tentar novamente...' 
                        : t('offlineTr.sentenceTranslator', 'Baixar tradutor (frases completas)')}
                    </span>
                    <div className="flex items-center gap-1.5 font-mono text-[11px] text-ink bg-tint-stone px-3 h-8 rounded-full group-hover:bg-primary group-hover:text-slate-900 transition-all duration-300">
                      <Download size={14} />
                      <span>900MB</span>
                    </div>
                  </button>
                )}
                {downloadError && (
                  <p className="text-xs text-bad font-medium mt-2">
                    {downloadError}
                  </p>
                )}

                {/* Dicionário offline (palavras) */}
                {!isProUser && !dictDownloaded && dictProgress === null ? (
                  <ProLockedRow label={t('offlineTr.wordDictionary', 'Tradutor com dicionário (palavras)')} size={dictSizeMB ? `${dictSizeMB}MB` : undefined} />
                ) : dictProgress !== null ? (
                  <div className="space-y-2 bg-surface p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
                    <div className="flex justify-between items-center text-xs font-medium text-slate-600 dark:text-slate-400">
                      <span>{t('offlineTr.downloading', 'Baixando...')}</span>
                      <span>{dictProgress}%{dictSizeMB ? ` · ${dictSizeMB}MB` : ''}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-ink transition-all duration-300" style={{ width: `${dictProgress}%` }} />
                    </div>
                  </div>
                ) : dictDownloaded ? (
                  <div className="flex items-center justify-between bg-tint-sage dark:bg-tint-sage/20 p-4 rounded-2xl border border-tint-sage dark:border-tint-sage/30">
                    <div className="flex items-center gap-2 text-ok font-medium text-sm">
                      <Check size={18} />
                      <span>{t('offlineTr.dictReady', 'Dicionário offline pronto')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {dictSizeMB && <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">{dictSizeMB}MB</span>}
                      <button
                        type="button"
                        onClick={handleDictDelete}
                        title={t('settings.translation.deleteOfflineModel', 'Excluir modelos do dispositivo')}
                        className="p-2 text-slate-400 hover:text-bad transition-colors cursor-pointer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleDictDownload}
                    className="w-full flex items-center justify-between bg-surface hover:bg-tint-stone/60 p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] transition-all duration-300 cursor-pointer group"
                  >
                    <span className="flex items-center gap-2 font-medium text-sm text-slate-700 dark:text-slate-300">
                      <BookOpen size={15} className="text-muted" />
                      {t('offlineTr.wordDictionary', 'Tradutor com dicionário (palavras)')}
                    </span>
                    <div className="flex items-center gap-1.5 font-mono text-[11px] text-ink bg-tint-stone px-3 h-8 rounded-full group-hover:bg-primary group-hover:text-slate-900 transition-all duration-300">
                      <Download size={14} />
                      <span>{dictSizeMB ? `${dictSizeMB}MB` : '—'}</span>
                    </div>
                  </button>
                )}
                {dictError && (
                  <p className="text-xs text-bad font-medium">
                    {t('offlineTr.downloadFailed', 'Falha ao baixar')}: {dictError}
                  </p>
                )}
              </div>
            </div>

            {/* Card 3: Áudio Pré-gravado */}
            <div data-tour="sec-tr-audio" className="bg-tint-stone/40 p-5 md:p-6 rounded-[22px] space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5 text-ink font-medium text-[15px] tracking-[-0.01em] [&_svg]:text-muted [&_svg]:w-4 [&_svg]:h-4">
                    <Volume2 size={18} />
                    <span>{t('settings.translation.usePrerecordedAudio', 'Áudio Pré-gravado')}</span>
                  </div>
                  <p className="text-[13px] text-muted leading-relaxed">
                    {t('settings.translation.usePrerecordedAudioDesc', 'Quando ativada, dá preferência aos áudios gravados em estúdio para palavras individuais em Inglês, Francês, Italiano, Português, Espanhol, Alemão, Russo e Húngaro.')}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const newValue = !isPrerecordedAudioEnabledState;
                    setIsPrerecordedAudioEnabledState(newValue);
                    localStorage.setItem('libritui_use_prerecorded_audio', newValue ? 'true' : 'false');
                  }}
                  className={`relative inline-flex items-center h-7 w-12 shrink-0 cursor-pointer rounded-full p-0.5 transition-colors duration-300 ease-out focus:outline-none ${
                    isPrerecordedAudioEnabledState ? 'bg-ink' : 'bg-ink/15'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPrerecordedAudioEnabledState ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Card 4: Reprodução de Vozes Offline (PRO) */}
            <div data-tour="sec-tr-voices" className="bg-tint-stone/40 p-5 md:p-6 rounded-[22px] space-y-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2.5 text-ink font-medium text-[15px] tracking-[-0.01em] [&_svg]:text-muted [&_svg]:w-4 [&_svg]:h-4">
                  <Mic size={18} />
                  <span>{t('settings.translation.offlineVoicesTitle', 'Reprodução de vozes offline')}</span>
                </div>
                <p className="text-[13px] text-muted leading-relaxed">
                  {t('settings.translation.offlineVoicesDesc', 'Baixe os pacotes de voz neurais para ouvir a pronúncia correta mesmo sem conexão de rede.')}
                </p>
              </div>

              <div className="space-y-4 pt-1">
                {/* Voz de Francês */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-surface p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-tint-sky flex items-center justify-center text-lg">
                      🇫🇷
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-sm text-ink dark:text-slate-200 flex items-center gap-2">
                        <span>Francês</span>
                        <span className="text-base select-none">🇫🇷</span>
                      </div>
                      <div className="text-xs text-slate-400 font-medium">Voz feminina</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {voices['fr']?.downloading ? (
                      <div className="flex flex-col items-end gap-1 w-28 sm:w-36">
                        <div className="flex justify-between w-full text-[10px] font-normal uppercase tracking-[0.14em] font-mono text-slate-400">
                          <span>Baixando</span>
                          <span>{voices['fr']?.progress || 0}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-ink transition-all duration-300"
                            style={{ width: `${voices['fr']?.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    ) : voices['fr']?.installed ? (
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-xs text-ok font-normal uppercase font-mono tracking-[0.14em]">
                          <Check size={16} /> Concluído
                        </span>
                        <button
                          type="button"
                          onClick={() => deleteVoice('fr')}
                          className="w-9 h-9 rounded-full flex items-center justify-center text-muted hover:text-bad hover:bg-tint-rose transition-all duration-300 cursor-pointer"
                          title="Excluir Voz"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : !isProUser ? (
                      <button
                        type="button"
                        onClick={() => setActiveTab('upgrade')}
                        title={t('offlineTr.proOnlyDesc')}
                        className="h-10 px-4 bg-tint-butter text-warn rounded-full text-[12px] font-mono flex items-center gap-1.5 cursor-pointer"
                      >
                        <Lock size={13} /> {t('offlineTr.proOnly', 'Recurso PRO')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => downloadVoice('fr')}
                        className="group h-10 px-4 bg-primary text-slate-900 rounded-full text-[13px] font-medium transition-all duration-300 hover:opacity-90 active:scale-95 flex items-center gap-1.5 cursor-pointer"
                      >
                        <Download size={14} /> Baixar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'update':
        return (
          <div className="space-y-8">
            <div className="flex flex-col items-center justify-center gap-6 text-center py-4">

              <div className="space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted">{versionLabel}</p>
                <h2 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('settings.update.title')}</h2>
                <p className="text-slate-500 font-medium text-sm max-w-md mx-auto">{t('settings.update.description')}</p>
              </div>

              <button
                onClick={() => setShowUpdateConfirmModal(true)}
                className="group w-full sm:w-auto px-7 h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2"
              >
                <RotateCw size={15} strokeWidth={1.75} className="transition-transform duration-700 group-hover:rotate-180" />
                {t('settings.update.button')}
              </button>

              <ConfirmationModal
                isOpen={showUpdateConfirmModal}
                title={
                  updateModalTranslations[i18n.language?.slice(0, 2) || 'pt']?.title ||
                  updateModalTranslations['pt'].title
                }
                message=""
                onConfirm={async () => {
                  setShowUpdateConfirmModal(false);
                  await handleHardRefresh();
                }}
                onCancel={() => setShowUpdateConfirmModal(false)}
                confirmLabel={
                  updateModalTranslations[i18n.language?.slice(0, 2) || 'pt']?.confirm ||
                  updateModalTranslations['pt'].confirm
                }
                cancelLabel={
                  updateModalTranslations[i18n.language?.slice(0, 2) || 'pt']?.close ||
                  updateModalTranslations['pt'].close
                }
              />
            </div>
          </div>
        );

      case 'sounds':
        return <NotificationSoundSettings />;
      case 'upgrade':
        return (
          <div className="space-y-8">
            <div className="flex flex-col items-center justify-center gap-6 text-center py-4">

              <div className="space-y-4 w-full">
                
                <h2 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">
                  {subscriptionTier === 'pro' ? t('settings.upgrade.proStatus') : t('settings.upgrade.title')}
                </h2>

                <div className="py-8 px-6 rounded-[22px] bg-tint-butter/40 flex items-center justify-center">
                  <p className="text-slate-400 font-medium italic text-sm text-center">
                    {t('settings.upgrade.placeholder')}
                  </p>
                </div>
              </div>

              {subscriptionTier === 'pro' ? (
                <div
                  className="w-full max-w-md py-6 px-8 rounded-2xl flex flex-col items-center gap-2 border border-tint-butter/50 dark:border-tint-butter/30 bg-tint-butter/30 dark:bg-tint-butter/10"
                >
                  <div className="flex items-center gap-3 text-warn dark:text-warn font-normal uppercase font-mono tracking-[0.14em] text-xs">
                    <Check size={18} />
                    {t('settings.upgrade.pro_active', 'Você já é usuário Pro, aproveite!')}
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleUpgrade}
                  disabled={isUpgrading}
                  className={`group w-full max-w-md h-12 bg-ink text-surface rounded-full text-[13px] font-medium transition-all duration-300 flex items-center justify-center gap-2 ${isUpgrading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90 active:scale-[0.98]'}`}
                >
                  {isUpgrading ? (
                    <RotateCw className="animate-spin" size={20} />
                  ) : (
                    <Sparkles size={20} />
                  )}
                  {isUpgrading ? t('common.processing') : t('settings.upgrade.button')}
                </button>
              )}
            </div>
          </div>
        );

      case 'offline':
        // Hidden as per UX optimization request
        return null;

      case 'storage':
        return (
          <div className="space-y-8">
            <div className="space-y-8">

              <div className="space-y-4">
                <p className="text-sm font-medium text-slate-500 leading-relaxed dark:text-slate-400">
                  {t('settings.sync.googleDriveDesc', 'O LibriTui salva seus livros diretamente na sua própria conta do Google Drive. Assim, você mantém o controle total da sua biblioteca e acessa seus arquivos de qualquer aparelho, sem custos extras.')}
                </p>
                <div className="p-4 rounded-2xl bg-tint-sage/50 text-[12px] text-ink/70 leading-relaxed">
                  🔒 <strong>{t('settings.privacyGuaranteed')}</strong> {t('settings.sync.privacyText', 'O app cria uma pasta exclusiva e invisível para salvar seus livros. Nós não temos acesso a nenhuma outra foto, documento ou arquivo pessoal do seu Google Drive.')}
                </div>
              </div>

              {driveError && (
                <div className="p-4 rounded-xl bg-tint-rose dark:bg-tint-rose/20 border border-tint-rose dark:border-tint-rose/30 text-xs font-medium text-bad text-center">
                  ⚠️ {driveError}
                </div>
              )}

              {sdkLoading && (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <Loader2 className="animate-spin text-primary" size={28} />
                  <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-400">{t('settings.initializingGoogle')}</span>
                </div>
              )}

               {!sdkLoading && !driveToken && (
                <div className="pt-4 flex flex-col items-center">
                  <button
                    onClick={handleConnectDrive}
                    disabled={isDriveConnecting}
                    className="group relative px-7 h-12 w-full bg-primary text-slate-900 rounded-full text-[13px] font-medium transition-all duration-300 hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2 overflow-hidden"
                  >
                    {isDriveConnecting ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24">
                        <path d="M19.345 9.7L12.59 2.22c-.36-.4-1.02-.4-1.38 0L4.455 9.7c-.36.4-.36 1.05 0 1.45l6.755 7.48c.36.4 1.02.4 1.38 0l6.755-7.48c.36-.4.36-1.05 0-1.45zm-1.8.8l-4.96 5.5-4.95-5.5 4.95-5.5 4.96 5.5z"/>
                      </svg>
                    )}
                    {isDriveConnecting ? t('settings.sync.connecting', 'Conectando...') : t('settings.sync.connectDrive', 'Conectar Google Drive')}
                  </button>
                  <p className="text-[9px] font-normal text-slate-400 text-center mt-3 uppercase font-mono tracking-[0.14em]">
                    {t('settings.sync.secureConnection', 'Conexão segura via Google OAuth 2.0')}
                  </p>
                </div>
              )}

              {driveToken && (
                <div className="p-5 rounded-[22px] bg-tint-stone/40 space-y-5">
                  <div className="flex items-center gap-4">
                    {driveUser?.picture ? (
                      <img
                        src={driveUser.picture}
                        alt="Perfil"
                        className="w-14 h-14 rounded-full border border-primary/20 object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-lg">
                        {driveUser?.name?.[0]?.toUpperCase() || 'G'}
                      </div>
                    )}
                    <div className="space-y-1">
                      <h4 className="font-medium text-sm dark:text-ink leading-tight">
                        {driveUser?.name || t('settings.sync.connectedUser', 'Usuário Conectado')}
                      </h4>
                      <p className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">
                        {driveUser?.email || t('settings.sync.activeDrive', 'Google Drive Ativo')}
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
                    <button
                      onClick={() => setShowDisconnectModal(true)}
                      className="w-full h-11 rounded-full text-muted text-[13px] font-medium border border-black/[0.08] dark:border-white/[0.08] hover:bg-tint-rose hover:text-bad hover:border-transparent transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      {t('settings.sync.disconnectDrive', 'Desconectar Google Drive')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  async function handleUpgrade() {
    setIsUpgrading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Erro ao iniciar checkout:', err);
      alert('Erro ao iniciar checkout. Tente novamente.');
    } finally {
      setIsUpgrading(false);
    }
  }

  const logoutTranslations: Record<string, { title: string; cancel: string; confirm: string }> = {
    pt: {
      title: 'Tem certeza que deseja sair da conta?',
      cancel: 'Cancelar',
      confirm: 'Sair'
    },
    en: {
      title: 'Are you sure you want to log out?',
      cancel: 'Cancel',
      confirm: 'Log Out'
    },
    es: {
      title: '¿Estás seguro de que quieres cerrar sesión?',
      cancel: 'Cancelar',
      confirm: 'Cerrar Sesión'
    },
    fr: {
      title: 'Êtes-vous sûr de vouloir vous déconnecter?',
      cancel: 'Annuler',
      confirm: 'Se déconnecter'
    },
    de: {
      title: 'Möchten Sie sich wirklich abmelden?',
      cancel: 'Abbrechen',
      confirm: 'Abmelden'
    },
    it: {
      title: 'Sei sicuro di voler uscire?',
      cancel: 'Annulla',
      confirm: 'Esci'
    },
    hu: {
      title: 'Biztosan ki szeretne lépni?',
      cancel: 'Mégse',
      confirm: 'Kijelentkezés'
    },
    ja: {
      title: '本当にログアウトしますか？',
      cancel: 'キャンセル',
      confirm: 'ログアウト'
    },
    zh: {
      title: '您確定要退出登录吗？',
      cancel: '取消',
      confirm: '退出登录'
    },
    ru: {
      title: 'Вы уверены, что хотите выйти из аккаунта?',
      cancel: 'Отмена',
      confirm: 'Выйти'
    }
  };

  // APP_VERSION é um timestamp do build: mostra como data legível
  const versionLabel = /^\d{12,}$/.test(String(APP_VERSION))
    ? `${t('settings.update.versionOf', 'Versão de')} ${new Date(Number(APP_VERSION)).toLocaleDateString(i18n.language || 'pt-BR')}`
    : `v${APP_VERSION}`;

  const activeLang = i18n.language?.split('-')[0] || 'pt';
  const logoutText = logoutTranslations[activeLang] || logoutTranslations.pt;

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <AppBackButton fallbackTo="/personal-hub" />

      <StudyPageHeader tourKey="settings" title={t('settings.title')} hideTour />

      <div data-tour="settings-tabs" className="space-y-3">
        <div data-tour="settings-panel" className="space-y-3">
          {([
            { key: 'preferencias', icon: <Fingerprint />, tint: 'bg-tint-lavender', title: t('settings.tabs.preferencias'), subtitle: t('settings.tabs.preferences.subtitle', 'Identidade e aparência') },
            { key: 'translation', icon: <Languages />, tint: 'bg-tint-sky', title: t('settings.tabs.translation'), subtitle: t('settings.translation.sectionDesc', 'Idioma nativo, tradutor offline e vozes') },
            { key: 'storage', icon: <Cloud />, tint: 'bg-tint-sage', title: t('settings.tabs.sincronizacao', 'Sincronização'), subtitle: driveToken ? (driveUser?.email || t('settings.sync.activeDrive', 'Google Drive Ativo')) : t('settings.cloudSyncDesc') },
            { key: 'sounds', icon: <Bell />, tint: 'bg-tint-peach', title: t('soundSettings.title', 'Notificação & Som'), subtitle: t('soundSettings.subtitle', 'Avisos, sons e volume') },
            { key: 'update', icon: <RotateCw />, tint: 'bg-tint-peach', title: t('settings.tabs.update'), subtitle: versionLabel },
            { key: 'upgrade', icon: <Sparkles />, tint: 'bg-tint-butter', title: t('settings.tabs.upgrade'), subtitle: subscriptionTier === 'pro' ? t('settings.upgrade.proStatus') : t('settings.upgrade.title') },
          ] as { key: SettingsTab; icon: React.ReactNode; tint: string; title: string; subtitle: string }[]).map((section, i) => (
            <SettingSection
              key={section.key}
              index={i}
              icon={section.icon}
              tint={section.tint}
              title={section.title}
              subtitle={section.subtitle}
              open={openSection === section.key}
              onToggle={() => toggleSection(section.key)}
            >
              {openSection === section.key ? (
                <div data-tour="settings-section-content">
                  {SECTION_TOURS[section.key] && (
                    <div className="flex justify-end -mt-2 mb-4">
                      <PageTourButton tourKey={SECTION_TOURS[section.key]!} />
                    </div>
                  )}
                  {renderContent()}
                </div>
              ) : null}
            </SettingSection>
          ))}
        </div>

        <div className="pt-3 space-y-3">
          {user?.email === 'willianrgracec@gmail.com' && (
            <SettingSection index={5} icon={<FlaskConical />} tint="bg-tint-rose" title="Beta" subtitle="Painel restrito" onToggle={() => navigate('/settings/beta')} link />
          )}
          <SettingSection index={6} icon={<HelpCircle />} tint="bg-tint-stone" title={t('help.title', 'Ajuda e Suporte')} subtitle={t('help.subtitle', 'Dúvidas, termos e contato')} onToggle={() => navigate('/help')} link />
        </div>
      </div>

      <UpdateSuccessModal
        isOpen={showSuccess}
        onConfirm={handleConfirmUpdate}
      />

      {/* LANGUAGE CONFIRMATION MODAL */}
      {pendingLanguage && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setPendingLanguage(null)} />
          <div className="relative w-full max-w-sm bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="px-8 pt-10 pb-6 text-center">
              <div className="w-14 h-14 bg-tint-sky text-ink rounded-full flex items-center justify-center mx-auto mb-5">
                <Languages size={22} strokeWidth={1.75} />
              </div>
              <h2 className="text-xl font-medium text-ink tracking-[-0.02em] leading-snug">{t('settings.translation.confirm_title', 'Deseja alterar o idioma da interface?')}</h2>
            </div>
            <div className="px-8 pb-8 flex flex-col-reverse gap-2">
              <button onClick={() => setPendingLanguage(null)} className="h-11 rounded-full text-muted hover:text-ink text-[13px] font-medium transition-colors duration-300">{t('common.cancel', 'Cancelar')}</button>
              <button onClick={() => {
                if (pendingLanguage) {
                  setTargetLanguage(pendingLanguage.code);
                  i18n.changeLanguage(pendingLanguage.code);
                  setPendingLanguage(null);
                }
              }} className="h-12 rounded-full bg-primary text-slate-900 text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-300">{t('common.confirm', 'Confirmar')}</button>
            </div>
          </div>
        </div>
      )}

      {/* PRO UPGRADE MODAL */}
      {/* DELETE VOICE CONFIRMATION MODAL */}
      {showDeleteVoiceModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowDeleteVoiceModal(null)} />
          <div className="relative w-full max-w-sm bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="px-8 pt-10 pb-6 text-center">
              <div className="w-14 h-14 bg-tint-rose text-ink rounded-full flex items-center justify-center mx-auto mb-5">
                <Trash2 size={22} strokeWidth={1.75} />
              </div>
              <h2 className="text-xl font-medium text-ink tracking-[-0.02em] leading-snug">{t('settings.offline.delete_title', 'Excluir voz offline?')}</h2>
              <p className="text-[13px] leading-relaxed text-muted mt-2 px-2">{t('settings.offline.delete_message', 'Os arquivos de voz serão removidos do dispositivo. Você poderá baixá-los novamente a qualquer momento.')}</p>
            </div>
            <div className="px-8 pb-8 flex flex-col-reverse gap-2">
              <button onClick={() => setShowDeleteVoiceModal(null)} className="h-11 rounded-full text-muted hover:text-ink text-[13px] font-medium transition-colors duration-300">{t('common.cancel', 'Cancelar')}</button>
              <button onClick={async () => { await deleteVoice(showDeleteVoiceModal); offlineTtsEngine.clearCache(showDeleteVoiceModal); setShowDeleteVoiceModal(null); }} className="h-12 rounded-full bg-bad text-white text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-300">{t('settings.offline.delete', 'Excluir')}</button>
            </div>
          </div>
        </div>
      )}

      {showProModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-2xl animate-in fade-in duration-300">
          <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 md:p-10 max-w-sm w-full text-center space-y-6 animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="w-14 h-14 bg-tint-butter text-ink rounded-full flex items-center justify-center mx-auto">
               <Sparkles size={22} strokeWidth={1.75} />
            </div>
            
            <div className="space-y-3">
              <h3 className="text-xl font-medium tracking-[-0.03em] text-ink">{t('settings.upgrade.pro_only', 'Disponível apenas para usuários PRO')}</h3>
              <p className="text-sm text-slate-500 font-medium leading-relaxed italic">
                {t('settings.upgrade.pro_message', 'Torne-se PRO, receba o melhor da tradução e explicação literária. Apoie o nosso projeto!')}
              </p>
            </div>

            <div className="space-y-3 pt-4">
              <button 
                onClick={() => {
                  setShowProModal(false);
                  setActiveTab('upgrade');
                }}
                className="w-full h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-300"
              >
                {t('settings.upgrade.pro_button', 'Quero ser PRO')}
              </button>
              <button 
                onClick={() => setShowProModal(false)}
                className="w-full h-11 text-muted hover:text-ink text-[13px] font-medium transition-colors duration-300"
              >
                {t('settings.upgrade.pro_later', 'Agora não')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOGOUT CONFIRMATION MODAL */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowLogoutModal(false)} />
          <div className="relative w-full max-w-sm bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="px-8 pt-10 pb-6 text-center">
              <div className="w-14 h-14 bg-tint-rose text-ink rounded-full flex items-center justify-center mx-auto mb-5">
                <LogOut size={22} strokeWidth={1.75} />
              </div>
              <h2 className="text-xl font-medium text-ink tracking-[-0.02em] leading-snug">{logoutText.title}</h2>
            </div>
            <div className="px-8 pb-8 flex flex-col-reverse gap-2">
              <button onClick={() => setShowLogoutModal(false)} className="h-11 rounded-full text-muted hover:text-ink text-[13px] font-medium transition-colors duration-300">{logoutText.cancel}</button>
              <button onClick={handleLogout} className="h-12 rounded-full bg-bad text-white text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-300">{logoutText.confirm}</button>
            </div>
          </div>
        </div>
      )}
      {/* GOOGLE DRIVE DISCONNECT CONFIRMATION MODAL */}
      <ConfirmationModal
        isOpen={showDisconnectModal}
        title={t('settings.disconnectDriveConfirm')}
        message={t('settings.disconnectDriveWarning')}
        confirmLabel={t('common.confirm', 'Confirmar')}
        isDanger={true}
        onConfirm={handleDisconnectDrive}
        onCancel={() => setShowDisconnectModal(false)}
      />
    </div>
  );
};

const SECTION_EASE = [0.22, 1, 0.36, 1] as const;

/** Tour próprio de cada seção (botão "?" dentro da seção aberta). */
const SECTION_TOURS: Partial<Record<SettingsTab, TourKey>> = {
  preferencias: 'settings_preferences',
  translation: 'settings_translation',
  storage: 'settings_storage',
  sounds: 'settings_sounds',
  update: 'settings_update',
  upgrade: 'settings_upgrade',
};

/**
 * Seção de Configurações (Design 2.0): cartão com ícone em círculo pastel, título e uma linha de resumo.
 * Ao clicar expande (altura animada) e mostra o conteúdo; `link` vira só um atalho com seta.
 */
const SettingSection: React.FC<{
  index: number;
  icon: React.ReactNode;
  tint: string;
  title: string;
  subtitle?: string;
  open?: boolean;
  link?: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}> = ({ index, icon, tint, title, subtitle, open = false, link = false, onToggle, children }) => (
  <motion.section
    layout="position"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: SECTION_EASE, delay: index * 0.05 }}
    className={`bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden transition-shadow duration-500 ${open ? 'shadow-[0_24px_60px_-32px_rgba(0,0,0,0.25)]' : ''}`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={link ? undefined : open}
      className={`group w-full flex items-center gap-4 p-4 md:p-5 text-left transition-colors duration-300 ${open ? '' : 'hover:bg-tint-stone/40'}`}
    >
      <span className={`w-11 h-11 shrink-0 rounded-full ${tint} text-ink flex items-center justify-center transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110 group-hover:-rotate-6 [&_svg]:w-[18px] [&_svg]:h-[18px] [&_svg]:stroke-[1.75]`}>
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-medium tracking-[-0.01em] text-ink truncate">{title}</span>
        {subtitle && <span className="block text-[12px] text-muted truncate mt-0.5">{subtitle}</span>}
      </span>
      {link ? (
        <ChevronRight size={17} strokeWidth={1.75} className="shrink-0 text-muted transition-transform duration-300 group-hover:translate-x-1" />
      ) : (
        <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? 'bg-ink text-surface rotate-180' : 'text-muted group-hover:bg-tint-stone'}`}>
          <ChevronDown size={16} strokeWidth={1.75} />
        </span>
      )}
    </button>

    {!link && (
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: SECTION_EASE }}
          >
            <div className="px-4 md:px-6 pb-6 md:pb-7 pt-1">
              <div className="h-px bg-black/[0.06] dark:bg-white/[0.06] mb-6" />
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )}
  </motion.section>
);
